import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import { get, set, del } from 'idb-keyval'
import type { BannerType } from './types'
import type { SlotState } from 'redux/slotsSlice'

const ROOT = 'persist:root'
export const BLACKLISTED_KEYS = ['archived', 'appType', 'autoArchiveTime', 'createdAt', 'createdBy', 'deleted', 'devices', 'dirty', 'dynamicYieldCampaignId', 'name', 'order', 'orientation', 'published', 'slot', 'stale', 'updatedAt', 'updatedBy']

export const createIDBPersister = (idbValidKey: IDBValidKey = 'BILDIT'): Persister => ({
  persistClient: async (client: PersistedClient) => {
    await set(idbValidKey, client)
  },
  restoreClient: async () => {
    return await get<PersistedClient>(idbValidKey)
  },
  removeClient: async () => {
    await del(idbValidKey)
  }
})
export const localStorageSize = () => {
  try {
    return Object.keys(localStorage ?? {}).reduce((prev, cur) => prev + (Object.prototype.hasOwnProperty.call(localStorage, cur) ? localStorage[cur].length : 0), 0)
  } catch (ex: any) {
    console.error(`'Unable to read local storage': ${ex?.message}`)
    return 0
  }
}

// remove metadata from backend + unused data
const trimData = (banner: BannerType) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  BLACKLISTED_KEYS.forEach((key) => delete banner[key])
  banner?.variants.forEach((v) => {
    delete v?.code?.raw
    delete v?.metadata
  })
}

export const trimLocalStorage = (): void => {
  try {
    const current = localStorage.getItem(ROOT)
    if (current) {
      const obj = JSON.parse(current)
      // remove unused admin data
      delete obj.auth
      delete obj.banners
      delete obj.bannersCatalog

      // filter stale data
      const curSlots: SlotState = JSON.parse(obj.slots)
      const slots = curSlots.slots.filter((slot) => slot.banners.some((banner) => banner.webSlots?.length > 0))
      slots.forEach((s) => { s.banners.forEach((b) => { trimData(b) }) })
      obj.slots = JSON.stringify({
        ...curSlots,
        slots
      })

      // re-save smaller root key without stale data
      try {
        localStorage.setItem(ROOT, JSON.stringify(obj))
      } catch (storageErr: any) {
        console.error(`Unable to free more local storage space: ${storageErr?.message}`)
      }
    }
  } catch (ex: any) {
    console.error(`Unexpected error: ${ex?.message}`)
  }
}

export const saveToLocal = (key: string, data: string | any): boolean => {
  try {
    localStorage.setItem(key, typeof data === 'string' ? data : JSON.stringify(data))
    return true
  } catch (initErr) {
    console.warn('Failed to save, attempt to free space and retry', initErr)
    try {
      trimLocalStorage()
      localStorage.setItem(key, typeof data === 'string' ? data : JSON.stringify(data))
      return true
    } catch (storageErr: any) {
      console.error(`Unable to free more local storage space: ${storageErr?.message}`)
      return false
    }
  }
}
